// ============================================================
// DEALIN MVP - PostHog Analytics (deferred loading)
// The posthog-js library (182KB) is loaded dynamically after
// the first paint to avoid blocking the landing page render.
// Events fired before init are queued and flushed on ready.
// ============================================================

const POSTHOG_KEY = 'phc_PTARRyDsukg7XS2ODYZwKVxyH9DAaR3vycdeOraLn6u';
const POSTHOG_HOST = 'https://eu.i.posthog.com';
const L_COM_EXPERIMENT_STORAGE_KEY = 'ab:l-com:v1';

let posthogInstance: any = null;
let initialized = false;
let initPromise: Promise<void> | null = null;

type QueuedCall = { method: string; args: unknown[] };
const queue: QueuedCall[] = [];

function registerExperimentRedirects(ph: any) {
  ph.onFeatureFlags(() => {
    if (window.location.pathname === '/consulenza/consulenza-finanza-agevolata-bandi') {
      if (ph.getFeatureFlag('finanza-agevolata-e2-experiment') === 'e-finanza-agevolata-2') {
        window.location.replace('https://www.deal-in.it/e-finanza-agevolata-2');
      }
    }
  });
}

function flushQueue() {
  if (!posthogInstance) return;
  for (const { method, args } of queue) {
    const fn = (posthogInstance as any)[method];
    if (typeof fn === 'function') fn.apply(posthogInstance, args);
  }
  queue.length = 0;
}

function getActiveExperimentProps(rawPath: string): Record<string, unknown> {
  if (typeof window === 'undefined') {
    return {};
  }

  const path = rawPath.split('?')[0];
  if (path !== '/l-com' && path !== '/l-com-a' && path !== '/l-com-b') {
    return {};
  }

  let variant: 'a' | 'b' | null = null;

  if (path === '/l-com-a') {
    variant = 'a';
  } else if (path === '/l-com-b') {
    variant = 'b';
  } else {
    const storedVariant = window.localStorage.getItem(L_COM_EXPERIMENT_STORAGE_KEY);
    if (storedVariant === 'lCom') {
      variant = 'a';
    } else if (storedVariant === 'lComB') {
      variant = 'b';
    }
  }

  if (!variant) {
    return {};
  }

  return {
    landing_experiment_key: 'l-com-ab-v1',
    landing_variant: variant,
  };
}

export function initPostHog() {
  if (initPromise) return initPromise;

  initPromise = new Promise<void>((resolve) => {
    const doInit = () => {
      import('posthog-js').then((mod) => {
        const ph = mod.default;
        ph.init(POSTHOG_KEY, {
          api_host: POSTHOG_HOST,
          ui_host: 'https://eu.posthog.com',
          capture_pageview: false,
          capture_pageleave: true,
          autocapture: true,
          capture_performance: true,
          persistence: 'localStorage+cookie',
          cross_subdomain_cookie: false,
          secure_cookie: window.location.protocol === 'https:',
          session_recording: {
            maskAllInputs: false,
            maskInputOptions: { password: true },
          },
        });
        registerExperimentRedirects(ph);
        posthogInstance = ph;
        initialized = true;
        flushQueue();
        resolve();
      });
    };

    if ('requestIdleCallback' in window) {
      (window as any).requestIdleCallback(doInit, { timeout: 3500 });
    } else {
      setTimeout(doInit, 2000);
    }
  });

  return initPromise;
}

export function isPostHogReady() {
  return initialized;
}

// ── Core helpers (queue-safe) ────────────────────────────────

export function identifyUser(userId: number, properties?: Record<string, unknown>) {
  if (posthogInstance) {
    posthogInstance.identify(String(userId), properties);
  } else {
    queue.push({ method: 'identify', args: [String(userId), properties] });
  }
}

export function resetUser() {
  if (posthogInstance) {
    posthogInstance.reset();
  } else {
    queue.push({ method: 'reset', args: [] });
  }
}

export function capturePageView(path: string, search?: string) {
  const props = {
    $current_url: window.location.origin + path + (search || ''),
    path,
    search: search || '',
    ...getActiveExperimentProps(path),
  };
  if (posthogInstance) {
    posthogInstance.capture('$pageview', props);
  } else {
    queue.push({ method: 'capture', args: ['$pageview', props] });
  }
}

export function captureEvent(event: string, properties?: Record<string, unknown>) {
  const mergedProperties = {
    ...getActiveExperimentProps(window.location.pathname),
    ...(properties || {}),
  };
  if (posthogInstance) {
    posthogInstance.capture(event, mergedProperties);
  } else {
    queue.push({ method: 'capture', args: [event, mergedProperties] });
  }
}

// ── Pre-defined event helpers ───────────────────────────────

export const PHEvents = {
  login: () => captureEvent('user_logged_in'),
  signup: (role: 'vendor' | 'client') => captureEvent('user_signed_up', { role }),
  logout: () => captureEvent('user_logged_out'),

  search: (query: string, resultsCount?: number) =>
    captureEvent('search_performed', { query, results_count: resultsCount }),
  filterApplied: (filters: Record<string, unknown>) =>
    captureEvent('filter_applied', filters),

  viewVendor: (vendorId: number, vendorName?: string) =>
    captureEvent('vendor_profile_viewed', { vendor_id: vendorId, vendor_name: vendorName }),
  viewService: (serviceId: number, vendorId: number, serviceName?: string) =>
    captureEvent('service_viewed', { service_id: serviceId, vendor_id: vendorId, service_name: serviceName }),
  contactVendor: (vendorId: number, method: 'chat' | 'consultation_request') =>
    captureEvent('vendor_contacted', { vendor_id: vendorId, method }),

  chatOpened: (threadId: number) => captureEvent('chat_opened', { thread_id: threadId }),
  messageSent: (threadId: number) => captureEvent('message_sent', { thread_id: threadId }),
  assessmentShared: (threadId: number, sessionId: number) =>
    captureEvent('assessment_shared_in_chat', { thread_id: threadId, session_id: sessionId }),

  briefStarted: () => captureEvent('brief_started'),
  briefStepCompleted: (step: number, totalSteps: number) =>
    captureEvent('brief_step_completed', { step, total_steps: totalSteps }),
  briefCompleted: (sessionId: number) =>
    captureEvent('brief_completed', { session_id: sessionId }),
  briefRateLimited: (isAnonymous: boolean) =>
    captureEvent('brief_rate_limited', { is_anonymous: isAnonymous }),

  consultationRequested: (vendorId: number, vendorName?: string) =>
    captureEvent('consultation_requested', { vendor_id: vendorId, vendor_name: vendorName }),

  profileUpdated: (section?: string) =>
    captureEvent('vendor_profile_updated', { section }),
  serviceCreated: (serviceId?: number) =>
    captureEvent('service_created', { service_id: serviceId }),
  serviceEdited: (serviceId: number) =>
    captureEvent('service_edited', { service_id: serviceId }),

  ctaClicked: (ctaName: string, location: string) =>
    captureEvent('cta_clicked', { cta_name: ctaName, location }),
};

// Re-export posthog instance getter for advanced usage
export const posthog = new Proxy({} as any, {
  get(_target, prop) {
    if (posthogInstance) return (posthogInstance as any)[prop];
    return (...args: unknown[]) => {
      queue.push({ method: String(prop), args });
    };
  },
});
